// One door to the T&M endpoints (tm_pricing_context, tm_runs, tm_finalize, tm_reopen, ...).
//
// tmRequest(method, path, body) NEVER throws. It resolves to
//   { ok, status, data, detail, code, errors, text }
// where `text` is a sentence a person can act on, built from the API's own detail
// (detailToString) and the T&M status table (ui-plan.md §2). A network failure is status 0.
// Callers add the context-specific parts (e.g. finalize's "No answer — the approval may have gone
// through" + Check again) on top of `status`.

import config from '../config.js';
import { detailToString } from './apiError';

export const TM_PIN_401_TEXT = "That PIN doesn't match an active user. Try again.";
export const TM_503_TEXT = 'Reading the cost data took too long; nothing was saved.';
export const TM_NO_ANSWER_TEXT = 'No answer from the server. The request may have gone through.';

/** Sentence for a failed T&M response (status + parsed detail). */
export function tmErrorText(status, detail) {
  const said = detailToString(detail);
  switch (status) {
    case 0:
    case 502:
    case 504:
      return TM_NO_ANSWER_TEXT;
    case 401:
      // Only a PIN refusal ("Invalid PIN", "A PIN is required.") is about the PIN. Any other 401
      // (the API-key middleware's "Invalid or missing API key." on a request that sent no PIN) is
      // shown as the API said it, never as a wrong PIN.
      if (!said || /\bPIN\b/i.test(said)) return TM_PIN_401_TEXT;
      return `${said} Refresh the page.`;
    case 403:
      return said || 'Only an Admin or Manager can do that.';
    case 404:
      return `${said || 'Not found.'} Refresh the page.`;
    case 503:
      return TM_503_TEXT;
    case 500:
      return `${said || 'The server hit an unexpected error.'} Nothing was saved. Tell Mike.`;
    default:
      return said || `The request failed (HTTP ${status}).`;
  }
}

export async function tmRequest(method, path, body) {
  const url = `${config.API_BASE_URL}${path}`;
  const opts = { method: (method || 'GET').toUpperCase() };
  if (body !== undefined) {
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(url, opts);
  } catch (e) {
    return { ok: false, status: 0, data: null, detail: null, code: null, errors: [], text: TM_NO_ANSWER_TEXT };
  }
  if (!res) return { ok: false, status: 0, data: null, detail: null, code: null, errors: [], text: TM_NO_ANSWER_TEXT };
  let raw = '';
  let parsed = null;
  try {
    if (typeof res.text === 'function') raw = await res.text();
    else if (typeof res.json === 'function') parsed = await res.json();
  } catch (e) {
    raw = '';
  }
  if (raw) {
    try { parsed = JSON.parse(raw); } catch (e) { parsed = null; }
  }
  if (res.ok) {
    return { ok: true, status: res.status, data: parsed, detail: null, code: null, errors: [], text: '' };
  }
  const detail = parsed && typeof parsed === 'object' && 'detail' in parsed ? parsed.detail : (parsed ?? (raw && raw.length < 300 && !/^\s*</.test(raw) ? raw : null));
  const code = detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail.code || detail.error || null) : null;
  const errors = detail && typeof detail === 'object' && Array.isArray(detail.errors) ? detail.errors.map(String) : [];
  return { ok: false, status: res.status, data: parsed, detail, code, errors, text: tmErrorText(res.status, detail) };
}
